import { displayValue } from './content';

/**
 * An example of what belongs in a column, shown in the empty box.
 *
 * The owner is the one person the app never tells anything. A generated app decides for
 * itself whether `day` holds a weekday or a date, and reads it back assuming the shape it
 * chose; the owner then types the other one into a form that shows nothing but the field's
 * name. What they get is a page with a heading saying "2 shifts this week" and nothing
 * under it — no error, because nothing went wrong as far as the app is concerned.
 *
 * A row already in the collection is the best possible example, because it is what the
 * app itself wrote. Only when there is nothing to copy is the name all we have.
 */
export function columnExample(field: string, existingRows: Record<string, unknown>[] = []): string {
  for (const row of existingRows) {
    const shown = displayValue(row[field]);
    if (shown.trim()) return shown;
  }
  return inferredExample(field);
}

/** Two days out: far enough to read as an example rather than as today's real date. */
function soon(): Date {
  const d = new Date();
  d.setDate(d.getDate() + 2);
  d.setHours(18, 0, 0, 0);
  return d;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function inferredExample(field: string): string {
  const f = field.toLowerCase();
  const d = soon();
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  if (/email|mail/.test(f)) return 'name@example.com';
  if (/phone|mobile|tel/.test(f)) return '07700 900123';
  if (/url|link|website/.test(f)) return 'https://example.com';

  /* A date and a time are the two the owner gets wrong, and they are wrong in opposite
     directions: a field called `day` alongside a `startsAt` holds the date only, and the
     time lives in the other one. Suggesting a whole timestamp for both is what produced a
     shift running from 6:00 PM to 6:00 PM. */
  if (/^day$|^date$/.test(f)) return date;
  /* An end that reads the same as the start is worse than no example: an owner copying
     both literally recreates the shift that ran from 6:00 PM to 6:00 PM. */
  if (/(?:end|finish|until)s?(?:at)?$/.test(f)) return `${date} 20:00`;
  if (/time$|^at$|^when$/.test(f) || /starts?(?:at)?$/.test(f)) return `${date} 18:00`;
  if (/date|day|when/.test(f)) return date;

  if (/min(?:s|utes)?$|duration|length/.test(f)) return '90';
  if (/price|cost|fee|amount/.test(f)) return '20';
  if (/needed|limit|places|capacity|spots|max|seats|size|total|count|quantity|qty/.test(f)) return '4';
  if (/^is[A-Z]|^has[A-Z]|active|enabled|published/.test(field)) return 'true';

  if (/desc|detail|note|about|summary|info|blurb/.test(f)) return 'A sentence or two.';
  if (/venue|location|where|room|address/.test(f)) return 'The main room';
  if (/person|staff|who|assignee|owner|host|lead/.test(f)) return 'Sam';
  if (/role|position|job|task/.test(f)) return 'Front of house';
  if (/name|title|label/.test(f)) return 'Friday evening';

  return '';
}
